
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React, { Fragment, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useAtom, Atom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import useTranslation from 'next-translate/useTranslation';
import QuotationForm from '../../containers/QuotationForm';
import shipmentAtom, {
  originCountryBaseAtom,
  destinationCountryBaseAtom,
  typeAtom,
  parcelsAtom,
  parcelMapAtom,
} from '../../contexts/shipment';
import { getPresetSize, getMatchedPresetSize } from '../../containers/Packages/utils';
import { PresetId } from '../../common/package';
import { Main } from '../../containers/Layout/styled';
import { Parcel, ShipmentType } from '../../types/common';
import { getBaseCountry } from '../../utils/helpers';
import {
  slugToParcelSize,
  slugToType,
  parseRoute,
  getMatchedSlug,
  getRoute,
} from '../../slug/ship';
import useSlug from '../../hooks/useSlug';
import useAuth from '../../contexts/auth';
import useCoupon from '../../hooks/useCoupon';
import useReferrerCode from '../../hooks/useReferrerCode';

interface Props {
  presetOriginCountry: string;
  presetDestinationCountry: string;
  presetType: ShipmentType;
  presetParcelId: string;
  presetParcel: Parcel;
}

const CreateShipmentPage: React.FC<Props> = ({
  presetOriginCountry,
  presetDestinationCountry,
  presetType,
  presetParcelId,
  presetParcel,
}) => {
  const { t } = useTranslation('common');
  const [{ authenticated }] = useAuth();
  const { addCouponToUser } = useCoupon();
  const { setRefereeCouponAlert } = useReferrerCode();

  useHydrateAtoms([
    [originCountryBaseAtom, presetOriginCountry],
    [destinationCountryBaseAtom, presetDestinationCountry],
    [typeAtom, presetType],
    [parcelsAtom, [presetParcelId]],
    [parcelMapAtom, { [presetParcelId]: presetParcel }],
  ] as Iterable<readonly [Atom<unknown>, unknown]>);

  const [originCountry] = useAtom(originCountryBaseAtom);
  const [destinationCountry] = useAtom(destinationCountryBaseAtom);
  const [type] = useAtom(typeAtom);
  const [shipment] = useAtom(shipmentAtom);
  const { parcels } = shipment;
  const preset = parcels.length === 1 ? (getMatchedPresetSize(parcels[0], type) as PresetId) : null;
  const slug = getMatchedSlug(parcels, type) + '/' + getRoute(originCountry, destinationCountry);
  useSlug('ship/' + slug, [originCountry, destinationCountry, JSON.stringify(parcels)]);

  const description: Record<PresetId, string> = {
    envelope: t('Ship.parcelEnvelope'),
    small: t('Ship.parcelSmall'),
    medium: t('Ship.parcelMedium'),
    large: t('Ship.parcelLarge'),
  };

  const getTitle = (
    originCountry: string,
    destinationCountry: string,
    parcelId: PresetId | null,
  ) => {
    const origin = originCountry ? t(`countries:Country.${originCountry}`) : ``;
    const destination = destinationCountry ? t(`countries:Country.${destinationCountry}`) : ``;
    const parcelSize = parcelId ? description[parcelId] : t('Ship.parcelCustom');
    if (!destinationCountry) return t(`Ship.titleNoDestination`, { origin, parcelSize });
    return t(`Ship.title`, { origin, destination, parcelSize });
  };

  useEffect(() => {
    if (authenticated) {
      addCouponToUser();
      setRefereeCouponAlert();
    }
  }, [authenticated]);

  return (
    <Fragment>
      <Head>
        <title>{`${getTitle(originCountry, destinationCountry, preset)} | ${t(
          'SEO.siteTitle',
        )}`}</title>
      </Head>
      <Main className="container">
        <QuotationForm />
      </Main>
    </Fragment>
  );
};

 const _getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;
  const { slug } = query;

  const [parcel, route] = (slug || []) as string[];

  const presetParcelId = uuid();
  const presetParcel = slugToParcelSize[parcel] || getPresetSize('small', ShipmentType.PARCEL);
  const { originCountry, destinationCountry } = parseRoute(route) || {};
  const presetType = slugToType[parcel] || ShipmentType.PARCEL;

  return {
    props: {
      presetOriginCountry: originCountry || getBaseCountry(),
      presetDestinationCountry: destinationCountry || '',
      presetType,
      presetParcelId,
      presetParcel,
    },
  };
};

export default CreateShipmentPage;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/ship/[[...slug]]',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  